/**
 * AdminDashboard — visão de gestão (Administrador / Administração).
 *
 * Responde à pergunta: "O negócio está saudável? Onde está bloqueado? Quem
 * produz e com que rentabilidade?".
 *
 * Layout (alinhado com a referência nflow-style):
 *   ▸ 5 KPIs no topo: total RFQs, conversão, receita ganha, pipeline,
 *     bloqueios > 7 dias.
 *   ▸ 4 charts no meio: motivos de perda (donut), funil temporal (linhas
 *     created/won/lost), pipeline por estado (donut), PnL audit por comercial
 *     (barras horizontais empilhadas — pediste explicitamente esta vista).
 *   ▸ 2 tabelas operacionais: top clientes (rentabilidade) + RFQs em G5
 *     a aguardar a Administração.
 */
import { Link } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import {
  KpiGrid, DonutChartCard, TimeSeriesCard, StackedBarCard, OperationalTable,
  HorizontalBarCard,
} from '@/components/dashboard';
import AlloyPriceEvolutionCard from '@/components/dashboard/AlloyPriceEvolutionCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatEuro, formatNumber, formatDate } from '@/lib/utils';
import { t } from '@/i18n/translations';

const STRATEGIC_VARIANT = {
  strategic: 'success',
  standard: 'secondary',
  transactional: 'muted',
};

export default function AdminDashboard({ data, lang }) {
  const charts = data.charts || {};
  const tables = data.tables || {};

  return (
    <div className="space-y-6">
      <KpiGrid kpis={data.kpis || []} lang={lang} />

      {/* Faixa central — 4 charts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <DonutChartCard
          title={t('chart_loss_reasons', lang)}
          data={(charts.loss_reasons || []).map((d) => ({
            key: `loss_reason_${d.key}`, count: d.count,
          }))}
          lang={lang}
          valueLabel="lost_total"
        />
        <TimeSeriesCard
          title={t('chart_funnel_timeseries', lang)}
          data={charts.funnel_timeseries || []}
          series={[
            { key: 'created', labelKey: 'series_created', color: '#3B82F6' },
            { key: 'won',     labelKey: 'series_won',     color: '#10B981' },
            { key: 'lost',    labelKey: 'series_lost',    color: '#EF4444' },
          ]}
          kind="line"
          lang={lang}
        />
        <DonutChartCard
          title={t('chart_pipeline_by_status', lang)}
          data={charts.pipeline_by_status || []}
          lang={lang}
        />
        <StackedBarCard
          title={t('chart_pnl_by_commercial', lang)}
          data={charts.pnl_by_commercial || []}
          lang={lang}
        />
      </div>

      {/* Distribuição de máquinas e operações de acabamento em RFQs ganhas
          — perfis Admin/Eng/Comercial todos consomem este par. Mostra o que
          a "fábrica vencedora" historicamente usa. */}
      <div className="grid gap-4 lg:grid-cols-2">
        <HorizontalBarCard
          title={t('chart_machines_won', lang)}
          data={charts.machines_won || []}
          lang={lang}
          emptyKey="no_won_data"
        />
        <HorizontalBarCard
          title={t('chart_finishing_won', lang)}
          data={charts.finishing_won || []}
          lang={lang}
          showPercent
          emptyKey="no_won_data"
        />
      </div>

      {/* Evolução de preços das ligas de alumínio (histórico de versões) */}
      <AlloyPriceEvolutionCard lang={lang} />

      {/* Tabelas operacionais */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-success" aria-hidden="true" />
              {t('table_top_clients', lang)}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {(tables.top_clients || []).length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground">
                {t('no_data', lang)}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-4 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      {t('client', lang)}
                    </th>
                    <th className="px-4 py-2 text-right text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      {t('won_revenue_short', lang)}
                    </th>
                    <th className="px-4 py-2 text-right text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      {t('won_count_short', lang)}
                    </th>
                    <th className="px-4 py-2 text-right text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      {t('conversion_short', lang)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(tables.top_clients || []).map((c) => (
                    <tr key={c.id} className="border-b border-border/50 last:border-0">
                      <td className="px-4 py-2">
                        <div className="font-medium">{c.name}</div>
                        <Badge variant={STRATEGIC_VARIANT[c.strategic_type] || 'muted'}
                               className="mt-0.5 text-[10px]">
                          {t(`client_type_${c.strategic_type}`, lang)}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-right tnum">
                        {formatEuro(c.won_revenue, lang)}
                      </td>
                      <td className="px-4 py-2 text-right tnum">
                        {formatNumber(c.won_count, lang)}
                      </td>
                      <td className="px-4 py-2 text-right tnum">
                        {c.conversion_pct == null ? '—' : `${formatNumber(c.conversion_pct, lang, 1)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <OperationalTable
          title={t('table_awaiting_direction', lang)}
          items={tables.awaiting_direction || []}
          lang={lang}
          columns={[
            { key: 'reference_number', labelKey: 'rfq_ref',
              render: (r) => (
                <Link to={`/rfqs/${r.id}`} className="font-medium hover:underline">
                  {r.reference_number}
                </Link>
              ),
            },
            { key: 'part_designation', labelKey: 'part_designation',
              render: (r) => (
                <span className="text-muted-foreground">{r.part_designation}</span>
              ),
            },
            { key: 'updated_at', labelKey: 'last_update', align: 'right',
              render: (r) => (
                <span className="tnum text-muted-foreground">
                  {formatDate(r.updated_at, lang)}
                </span>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
